import { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { ToastProvider } from './components/Toast';
import { SplashScreen } from './components/SplashScreen';
import { PromoPopup } from './components/PromoPopup';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ChatNotificationProvider } from './components/ChatNotificationProvider';
import { Home } from './pages/Home';
import { VerifyEmail } from './pages/VerifyEmail';
import { ResetPassword } from './pages/ResetPassword';
import { Account } from './pages/Account';
import { AdDetail } from './pages/AdDetail';
import { ChatPage } from './pages/ChatPage';
import { InboxPage } from './pages/InboxPage';
import { PostAd } from './pages/PostAd';
import { AdsListingPage } from './pages/AdsListingPage';
import { CategoryPage } from './pages/CategoryPage';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { Support } from './pages/Support';

export default function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [showPromo, setShowPromo] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Capture referral code from URL and store in sessionStorage
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      sessionStorage.setItem('nimbazaar_referral', ref);
    }
  }, [searchParams]);

  // Check if we should show layout (navbar/footer)
  const noLayoutRoutes = ['/verify-email', '/reset-password'];
  const noFooterRoutes = ['/inbox'];
  const showLayout = !noLayoutRoutes.some(
    (r) => location.pathname === r || location.pathname.startsWith(r + '/')
  );

  // Show promo popup after splash screen closes (only once per session)
  const handleSplashComplete = () => {
    setShowSplash(false);

    // Check if promo was already shown this session
    const promoShown = sessionStorage.getItem('promoShown');
    if (!promoShown && location.pathname === '/') {
      // Small delay before showing promo
      setTimeout(() => {
        setShowPromo(true);
      }, 500);
    }
  };

  const handlePromoClose = () => {
    setShowPromo(false);
    sessionStorage.setItem('promoShown', 'true');
  };

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <ToastProvider>
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      {/* {showPromo && <PromoPopup onClose={handlePromoClose} />} */}

      <ChatNotificationProvider />
      <div className="min-h-screen bg-white flex flex-col">
        {showLayout && <Navbar />}

        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/ads" element={<AdsListingPage />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/ad/:slug" element={<AdDetail />} />
            <Route
              path="/chat/:adId/:sellerId"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <ProtectedRoute>
                  <InboxPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inbox/:conversationId"
              element={
                <ProtectedRoute>
                  <InboxPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/post-ad"
              element={
                <ProtectedRoute>
                  <PostAd />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-ad/:id"
              element={
                <ProtectedRoute>
                  <PostAd />
                </ProtectedRoute>
              }
            />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/support" element={<Support />} />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/:tab"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>

        {showLayout && !noFooterRoutes.some((r) => location.pathname === r || location.pathname.startsWith(r + '/')) && <Footer />}
      </div>
    </ToastProvider>
  );
}
